import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../api/api";
import { endPoints } from "../../constants/endPoints";
import { ERROR_MESSAGES } from "../../constants/enums";
import {
  ICalculatePbsQuotePayload,
  ICreatePbsQuotePayload,
  IPbsQuoteHistoryFilters,
  IPbsQuoteLog,
  IPbsQuoteRejectValue,
  IPbsQuoteResult,
  IRenewalPbsQuotePayload,
} from "./pbsQuote.types";

// Backend envelope: { success, message, data }
interface IPbsResponseEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
}

// Maps an axios error into the reject value, capturing the 422 `errors` array.
const mapPbsError = (err: any): IPbsQuoteRejectValue => {
  const status: number | undefined = err?.response?.status;
  const data = err?.response?.data;
  const errors: string[] | undefined = Array.isArray(data?.data?.errors)
    ? data.data.errors
    : Array.isArray(data?.errors)
    ? data.errors
    : undefined;

  return {
    message: data?.message || ERROR_MESSAGES.SERVER_ERROR,
    status,
    errors,
  };
};

interface ICreateQuoteResponseData extends IPbsQuoteResult {
  pfaBase64?: string | null;
}

export const calculatePbsQuote = createAsyncThunk<
  { data: IPbsQuoteResult },
  ICalculatePbsQuotePayload,
  { rejectValue: IPbsQuoteRejectValue }
>("pbsQuote/calculate", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.post<IPbsResponseEnvelope<IPbsQuoteResult>>(
      endPoints.pbs.quote.calculate,
      payload
    );
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue(mapPbsError(err));
  }
});

export const createPbsQuote = createAsyncThunk<
  { data: ICreateQuoteResponseData },
  ICreatePbsQuotePayload,
  { rejectValue: IPbsQuoteRejectValue }
>("pbsQuote/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.post<IPbsResponseEnvelope<ICreateQuoteResponseData>>(
      endPoints.pbs.quote.create,
      payload
    );
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue(mapPbsError(err));
  }
});

export const createRenewalPbsQuote = createAsyncThunk<
  { data: ICreateQuoteResponseData },
  IRenewalPbsQuotePayload,
  { rejectValue: IPbsQuoteRejectValue }
>("pbsQuote/renewal", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.post<IPbsResponseEnvelope<ICreateQuoteResponseData>>(
      endPoints.pbs.quote.renewal,
      payload
    );
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue(mapPbsError(err));
  }
});

export const fetchPbsQuoteHistory = createAsyncThunk<
  { list: IPbsQuoteLog[]; totalRecords: number },
  IPbsQuoteHistoryFilters,
  { rejectValue: IPbsQuoteRejectValue }
>("pbsQuote/fetchHistory", async (filters, { rejectWithValue }) => {
  try {
    const params: Record<string, unknown> = {
      pageNo: filters.pageNo,
      itemPerPage: filters.itemPerPage,
    };
    if (filters.mode && filters.mode !== "all") params["mode"] = filters.mode;
    if (filters.from_date) params["from_date"] = filters.from_date;
    if (filters.to_date) params["to_date"] = filters.to_date;
    if (filters.quoteNumber) params["quoteNumber"] = filters.quoteNumber;

    const res = await api.get<
      IPbsResponseEnvelope<{ list: IPbsQuoteLog[]; totalRecords: number }>
    >(endPoints.pbs.quote.history, { params });

    return {
      list: res.data.data?.list ?? [],
      totalRecords: res.data.data?.totalRecords ?? 0,
    };
  } catch (err: any) {
    return rejectWithValue(mapPbsError(err));
  }
});
